import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsReadOnlyEmployee } from "../_helpers/useIsReadOnlyEmployee";
import { LicenseActionButtons } from "./LicenseActionButtons";
import {
    getLicenseStateConfig,
    hasLicenseActions,
    LICENSE_STATES,
} from "./licenseStates";
import './LicensesTable.css'; // Import the styles here

// Month of a date as an <input type="month"> gives it, in local time like the table shows it
const toMonthValue = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

// Created, renewed or due for renewal in the month
const isInMonth = (license, month) => [
    license.createdOn,
    license.validUntil,
    ...(license.renewalPeriodStarts ?? []),
].some(date => toMonthValue(date) === month);

export const LicensesTable = ({ licenses, licenseTypes, onLicenseChange }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [month, setMonth] = useState('');

    const { t } = useTranslation();
    const isReadOnly = useIsReadOnlyEmployee();

    const stateConfig = getLicenseStateConfig(t);

    const getLicenseType = (uid) => {
        const licenseType = licenseTypes.find(t => t.uid === uid);
        return licenseType;
    };

    // Helper para encontrar el nombre del tipo de licencia por UID
    const getLicenseName = (uid) => {
        const licenseType = getLicenseType(uid);
        return licenseType ? licenseType.name : `Unknown (${uid})`;
    };

    // Filtrar antes de paginar
    const searchTerm = search.trim().toLowerCase();
    const filteredLicenses = licenses.filter(item => {
        const matchesStatus = statusFilter === 'all' || item.status.toString() === statusFilter;
        const matchesType = typeFilter === 'all' || item.licenseTypeUID === typeFilter;
        const matchesSearch = !searchTerm
            || (item.locationName ?? '').toLowerCase().includes(searchTerm)
            || (item.locationOwnerEmail ?? '').toLowerCase().includes(searchTerm);
        const matchesMonth = !month || isInMonth(item, month);
        return matchesStatus && matchesType && matchesSearch && matchesMonth;
    });
    const expiredCount = licenses.filter(l => l.status === LICENSE_STATES.EXPIRED).length;
    const almostExpiredCount = licenses.filter(l => l.status === LICENSE_STATES.ALMOST_EXPIRED).length;
    const hasAlerts = expiredCount > 0 || almostExpiredCount > 0;

    // Paginación actualizada
    const totalPages = Math.ceil(filteredLicenses.length / itemsPerPage);
    const currentItems = filteredLicenses.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString();
    };

    return (
        <div className="w-100">
            <div className="card mb-4 bg-light">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 col-lg-3 mb-3 mb-lg-0">
                            <label className="font-weight-bold text-secondary">{t("mod_budgets_licenses_search")}</label>
                            <input
                                data-testid="licenses-search"
                                className="form-control"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                        <div className="col-md-6 col-lg-3 mb-3 mb-lg-0">
                            <label className="font-weight-bold text-secondary">{t("mod_budgets_license_type")}</label>
                            <select 
                                className="form-control" 
                                value={typeFilter} 
                                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">{t("mod_budgets_license_type_selector_all_types")}</option>
                                {licenseTypes.map(t => <option key={t.uid} value={t.uid}>{t.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6 col-lg-3 mb-3 mb-lg-0">
                            <label className="font-weight-bold text-secondary">{t("mod_budgets_licenses_table_state")}</label>
                            <select 
                                className="form-control" 
                                value={statusFilter} 
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">{t("mod_budgets_license_type_selector_all_states")}</option>
                                {Object.entries(stateConfig).map(([key, val]) => (
                                    <option key={key} value={key}>{val.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 col-lg-3 mb-3 mb-lg-0">
                            <label className="font-weight-bold text-secondary">{t("mod_budgets_licenses_month")}</label>
                            <input
                                data-testid="licenses-month"
                                type="month"
                                className="form-control"
                                value={month}
                                onChange={(e) => { setMonth(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {hasAlerts && (
                <div className="card mb-4 border-warning shadow-sm">
                    <div className="card-body p-3">
                        <h6 className="card-title font-weight-bold text-uppercase text-muted mb-3">
                            <i className="bi bi-bell-fill mr-2"></i>{t("mod_budgets_license_attention_required")}
                        </h6>
                        
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                            <div className="mb-2 mb-md-0">
                                {expiredCount > 0 && (
                                    <div className="mb-2 d-flex align-items-center">
                                        <span className="badge badge-danger mr-2">{expiredCount}</span>
                                        <span className="text-muted">{t("mod_budgets_licenses_expired_message")}</span>
                                        <button 
                                            className="btn btn-sm ml-2 font-weight-bold"
                                            onClick={() => { setStatusFilter(LICENSE_STATES.EXPIRED.toString()); setCurrentPage(1); }}
                                            style={{ 
                                                color: '#721c24', 
                                                backgroundColor: '#f8d7da', 
                                                border: '1px solid #f5c6cb',
                                                fontSize: '0.75rem',
                                                padding: '1px 8px'
                                            }}
                                        >
                                            {t("mod_budgets_licenses_view_expired")}
                                        </button>
                                    </div>
                                )}
                                
                                {almostExpiredCount > 0 && (
                                    <div className="d-flex align-items-center">
                                        <span className="badge badge-warning mr-2 text-dark">{almostExpiredCount}</span>
                                        <span className="text-muted">{t("mod_budgets_licenses_almost_expired_message")}</span>
                                        <button 
                                            className="btn btn-sm ml-2 font-weight-bold"
                                            onClick={() => { setStatusFilter(LICENSE_STATES.ALMOST_EXPIRED.toString()); setCurrentPage(1); }}
                                            style={{ 
                                                color: '#856404', // Marrón oscuro tipo "warning" de BS4 para legibilidad
                                                backgroundColor: '#fff3cd', // Fondo suave
                                                border: '1px solid #ffeeba',
                                                fontSize: '0.75rem',
                                                padding: '1px 8px'
                                            }}
                                        >
                                            {t("mod_budgets_licenses_view_almost_expired")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Botón opcional para ver todo de nuevo si el usuario se pierde */}
                            {(statusFilter !== 'all') && (
                                <button 
                                    className="btn btn-sm btn-outline-secondary mt-2 mt-md-0"
                                    onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setSearch(''); setCurrentPage(1); }}
                                >
                                    {t("mod_budgets_licenses_view_all")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="table-responsive border rounded shadow-sm bg-white">
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-light text-secondary">
                        <tr>
                            <th className="text-center pe-4">ID</th>
                            <th className="ps-4 py-3">{t("mod_budgets_licenses_table_type")}</th>
                            <th className="text-center">{t("mod_budgets_licenses_table_state")}</th>
                            <th>{t("mod_budgets_licenses_table_location")}</th>
                            <th className="text-center">{t("mod_budgets_licenses_table_created_on")}</th>
                            <th className="text-center">{t("mod_budgets_licenses_table_valid_until")}</th>
                            <th className="text-center pe-4">{t("mod_budgets_licenses_table_quantity")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((item, index) => {
                            const config = stateConfig[item.status] || { label: t("mod_budgets_licenses_table_type_unknown"), class: 'bg-dark' };
                            const displayLicenseTypeName = getLicenseName(item.licenseTypeUID);
                            const licenseType = getLicenseType(item.licenseTypeUID);
                            const hasActions = !isReadOnly && hasLicenseActions(item, licenseType);

                            return (
                                <tr key={item.uid} title={t("mod_budgets_licenses_table_manage_message")}>
                                    <td className="ps-4 fw-bold">{item.id}</td>
                                    <td className="ps-4 fw-bold">{displayLicenseTypeName}</td>
                                    <td className="text-center">
                                        <span className={`badge rounded-pill ${config.class}`} style={{ fontSize: '0.75rem' }}>
                                            {config.label}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="text-muted"><i className="bi bi-building me-1"></i></span>
                                        {item.locationName}
                                    </td>
                                    <td className="text-center text-muted">
                                        {formatDate(item.createdOn)}
                                    </td>
                                    <td className="text-center">
                                        <span className={item.status === LICENSE_STATES.EXPIRED ? 'text-danger fw-bold' : 'text-muted'}>
                                            {formatDate(item.validUntil)}
                                        </span>
                                    </td>
                                    <td className="text-center pe-4 font-monospace">
                                        {item.quantity}
                                    </td>
                                    <td className="text-right table-actions-cell">
                                        {hasActions && (
                                            <>
                                            <span className="action-hint pr-4" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                &#8942; {/* This is the Unicode for vertical ellipsis ⋮ */}
                                            </span>
                                            <LicenseActionButtons license={item} licenseType={licenseType} />
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Navegación de Paginación */}
            {totalPages > 1 && (
                <nav className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <div className="text-muted">
                        {t("mod_budgets_licenses_table_pagination_showing")}
                        <span className="font-weight-bold text-dark"> {currentItems.length} </span> 
                        {t("mod_budgets_licenses_table_pagination_of")} 
                        <span className="font-weight-bold text-dark"> {filteredLicenses.length} </span> 
                        {t("mod_budgets_licenses_table_pagination_licenses")} 
                    </div>
                    
                    <ul className="pagination pagination-sm mb-0">
                        {/* Botón Anterior */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                                className="page-link shadow-none" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                            >
                                &laquo; <span className="d-none d-sm-inline">{t("mod_budgets_licenses_table_pagination_button_previous")}</span>
                            </button>
                        </li>

                        {/* Números de página - Solo mostramos los necesarios si hay muchos */}
                        {[...Array(totalPages)].map((_, i) => {
                            const pageNum = i + 1;
                            // Opcional: Lógica para mostrar solo páginas cercanas a la actual
                            const isNear = Math.abs(currentPage - pageNum) <= 1;
                            const isEdge = pageNum === 1 || pageNum === totalPages;

                            if (totalPages > 7 && !isNear && !isEdge) return null;

                            return (
                                <li key={i} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                    <button 
                                        className="page-link shadow-none" 
                                        onClick={() => setCurrentPage(pageNum)}
                                        style={{ minWidth: '35px', textAlign: 'center' }}
                                    >
                                        {pageNum}
                                    </button>
                                </li>
                            );
                        })}

                        {/* Botón Siguiente */}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button 
                                className="page-link shadow-none" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                            >
                                <span className="d-none d-sm-inline">{t("mod_budgets_licenses_table_pagination_button_next")}</span> &raquo;
                            </button>
                        </li>
                    </ul>
                </nav>
            )}
        </div>
    );
};